import { IFeeValidationData } from 'types/order/fees/IFeeValidationData';
import { ISectionData, ISettlementItemData } from 'types/order/fees/ISectionData';
import { ISectionDefinitionData } from 'types/order/fees/ISectionDefinition';
import { eTransactionCategory } from '../../../../ui-toolkit/enums/eTransactionType';

interface IValidateAllProps {
  rules: IFeeValidationData[];
  sections: ISectionData[];
  sectionDefinitions: ISectionDefinitionData[];
  currentLineItem?: {
    settlementStatementItemId?: string;
    statementSettlementAndFeeCategoryDefinitionId?: string;
  };
  transactionCategory: eTransactionCategory;
}
export const validateAll = ({
  rules = [],
  currentLineItem,
  transactionCategory,
  sections,
  sectionDefinitions,
}: IValidateAllProps): string[] => {
  const getDefinition = (x: ISectionData) => {
    const result = sectionDefinitions.find((t) => t.statementSectionDefinitionId === x.statementSectionDefinitionId);
    return result;
  };
  return sections
    .filter((section) => !!getDefinition(section))
    .flatMap((section) =>
      validateSection({
        section,
        sectionDefinition: getDefinition(section)!,
        rules,
        currentLineItem,
        transactionCategory,
      })
    );
};

interface IValidateSectionProps {
  rules: IFeeValidationData[];
  section: ISectionData;
  sectionDefinition: ISectionDefinitionData;
  currentLineItem?: {
    settlementStatementItemId?: string;
    statementSettlementAndFeeCategoryDefinitionId?: string;
  };
  transactionCategory: eTransactionCategory;
}
const validateSection = ({
  section,
  sectionDefinition,
  rules,
  currentLineItem,
  transactionCategory,
}: IValidateSectionProps): string[] => {
  const dataLength = section?.statementSettlementItems?.length || 0;
  if (dataLength === 0) {
    return [];
  }
  const hasCurrentItem = !!currentLineItem;
  const isAdding =
    hasCurrentItem &&
    !section.statementSettlementItems.find(
      (t) => t.settlementStatementItemId === currentLineItem?.settlementStatementItemId
    );

  const sectionRule = rules.find(
    (rule) =>
      !!rule.statementSectionDefinitionId &&
      !rule.feeCategoryDefinitionId &&
      rule.statementSectionDefinitionId === sectionDefinition.statementSectionDefinitionId &&
      (rule.transactionCategory === 'all' || rule.transactionCategory === transactionCategory)
  );
  const lineItemRules = rules.filter(
    (rule) =>
      !!section.statementSettlementItems.find(
        (t) =>
          !!rule.feeCategoryDefinitionId &&
          t.statementSettlementAndFeeCategoryDefinitionId === rule.feeCategoryDefinitionId &&
          (rule.transactionCategory === 'all' || rule.transactionCategory === transactionCategory)
      )
  );
  const thisItemRule = rules.find(
    (rule) =>
      !!rule.feeCategoryDefinitionId &&
      rule.feeCategoryDefinitionId === currentLineItem?.statementSettlementAndFeeCategoryDefinitionId &&
      (rule.transactionCategory === 'all' || rule.transactionCategory === transactionCategory)
  );

  const sectionResults = applySectionRule(section.statementSettlementItems, isAdding, sectionRule);
  const lineItemResults = applyLineItemRules(section.statementSettlementItems, lineItemRules);
  const currentItemResults = applyLineItemRule(
    section.statementSettlementItems,
    isAdding,
    thisItemRule,
    currentLineItem
  );

  return [...sectionResults, ...(hasCurrentItem ? currentItemResults : lineItemResults)];
};

const applySectionRule = (data: ISettlementItemData[], isAdding: boolean, rule?: IFeeValidationData) => {
  if (!rule) {
    return [];
  }

  if (isAdding) {
    return data.length + 1 > rule.limit ? [rule.helpText] : [];
  }

  return data.length > rule.limit ? [rule.helpText] : [];
};

const applyLineItemRule = (
  data: ISettlementItemData[],
  isAdding: boolean,
  rule?: IFeeValidationData,
  currentLineItem?: {
    settlementStatementItemId?: string;
    statementSettlementAndFeeCategoryDefinitionId?: string;
  }
) => {
  if (!rule) {
    return [];
  }

  const itemCount = data.filter(
    (t) => t.statementSettlementAndFeeCategoryDefinitionId === rule.feeCategoryDefinitionId
  ).length;

  if (
    isAdding &&
    !!currentLineItem &&
    currentLineItem?.statementSettlementAndFeeCategoryDefinitionId === rule.feeCategoryDefinitionId
  ) {
    return itemCount + 1 > rule.limit ? [rule.helpText] : [];
  }

  return itemCount > rule.limit ? [rule.helpText] : [];
};

const applyLineItemRules = (data: ISettlementItemData[], rules: IFeeValidationData[]) => {
  return rules.flatMap((rule) => applyLineItemRule(data, false, rule));
};
